import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, FormControl, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";

import {
  alignStart,
  flexCenter,
  flexRow,
} from "../../../styles/mui/styles/display";
import { rules } from "../../../utils/validation/Validation";
import { FormInputText } from "../../../components/FormInputText";
import {
  GridContainer,
  InputStyle,
  PhoneNumberInputStyle,
  SelectInputStyle,
} from "../style/style";
import { FormSelect } from "../../../components/FormSelect";
import { MyAccountModel } from "../../../models/MyAccount";
import { getCurrnetPageFullPath, getSessionStorage, getUaserPageViewPayload, getValue } from "../../../utils";
import {
  NotificationLabel,
  UserDetailsActionDispatchTypes,
} from "../../../constants/MyAccount";
import { ListOptions } from "../../../models/Master";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";
import { AppConfigs } from "../../../models/AppConfigs";
import fontWeight from "../../../styles/mui/fontWeight";
import { colors } from "../../../styles/colors";
import { Asterisk } from "../../../styles/common/style";
import { UserPageTrack } from "../../../models/User";
import { getValidMessageType } from "../../../constants/User";
import { ApplicationTitles } from "../../../constants/HomePage";
import { redirectUri } from "../../../constants/Config";

export interface PropsFromState {
  config: AppConfigs;
  masterMfaPreferenceState: ListOptions[];
  updateUserDetailsState: MyAccountModel;
  loggedInUser: MyAccountModel;
  updateError: any;
  loggedInUserFlow: number;
}

export interface PropsFromDispatch {
  getMfaPreference: () => void;
  updateUserDetails: (payload: MyAccountModel) => void;
  getLoggedInUserByEmail: (userEmail: string) => void;
  resetStates: (actionType: string) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const MyAccount: React.FC<AllProps> = ({
  getMfaPreference,
  updateUserDetails,
  resetStates,
  masterMfaPreferenceState,
  updateUserDetailsState,
  loggedInUser,
  updateError,
  getLoggedInUserByEmail,
  loggedInUserFlow,
  postUserPageTrack
}) => {
  const methods = useForm<any>({
    defaultValues: {
      ...loggedInUser,
      providerPortalUser: {
        ...loggedInUser.providerPortalUser,
        mfaSSO: 4, //TBD need to update as part of future story linked to 21965/21614
      },
    },
  });

  const { handleSubmit, control, setError } = methods;
  const formRef = useRef(null);
  
  const onSubmit = (data: MyAccountModel) => {
    updateUserDetails({
      ...loggedInUser,
      providerPortalUser: {
        ...loggedInUser.providerPortalUser,
        email: loggedInUser.providerPortalUser.email,
        phoneNumber: data.providerPortalUser.phoneNumber,
        firstName: data.providerPortalUser.firstName,
        lastName: data.providerPortalUser.lastName,
        mfaId: data.providerPortalUser.mfaId
      },
    });
  };

  const submit = () => {
    handleSubmit(onSubmit)();
  };

  useEffect(() => {
     postUserPageTrack(
       getUaserPageViewPayload(
         getValidMessageType(ApplicationTitles.MY_ACCOUNT),
         getCurrnetPageFullPath(),
         redirectUri,
         ""
       )
     );
    if (loggedInUserFlow !== 1) {
      getMfaPreference();
    }
  }, []);

  useEffect(() => {
    if (
      !isEmpty(getValue(updateUserDetailsState, "providerPortalUser.userId"))
    ) {
      getLoggedInUserByEmail(getValue(updateUserDetailsState, "providerPortalUser.email"));
      resetStates(UserDetailsActionDispatchTypes.UPDATE_USER_DETAILS_RESET);
    }
  }, [updateUserDetailsState, resetStates]);

  useEffect(() => {
    if (!isEmpty(updateError) && !isEmpty(updateError.error)) {
        Object.keys(updateError.errors).forEach((field) => {
          updateError.errors[field].forEach((message: string) => {
            setError(field, {
              type: 'manual',
              message: message,
            });
          });
        });
    }
  }, [updateError, setError])
 const [mfaPreferenceOptions, setMfaPreferenceOptions] = React.useState<any[]>([]);
 const [ssoMfaPreferenceOption, setSsoMfaPreferenceOption] = React.useState<any[]>([]);

  const getMfaPreferenceOptions = () => {
    if (masterMfaPreferenceState && masterMfaPreferenceState.length > 0) {
      return masterMfaPreferenceState.filter((option: ListOptions) => option.value !== 4);
    } else {
      return [];
    }
  }

  const getSsoMfaPreferenceOption = () => {
    if (masterMfaPreferenceState && masterMfaPreferenceState.length > 0) {
      return masterMfaPreferenceState.filter((option: ListOptions) => option.value === 4);
    } else {
      return [];
    }
  }

  const checkIfSSOUser = () => {
    return getValue(loggedInUser, "providerPortalUser.isSso", null) === true && (getSessionStorage("samlToken") || "").length > 0;
  }

  useEffect(() => { 
    if (masterMfaPreferenceState && masterMfaPreferenceState.length > 0) {
      setMfaPreferenceOptions(getMfaPreferenceOptions());
      setSsoMfaPreferenceOption(getSsoMfaPreferenceOption());
    }
  }, [masterMfaPreferenceState]);

  const label = (label: string, mandate?: boolean) => {
    return (
      <Grid size={{ xs: 12 }} sx={flexRow}>
        <Typography
          variant="h4"
          color={colors.black[17]}
          fontWeight={fontWeight[2]}
        >
          {label}
        </Typography>
        {mandate && (
          <Typography
            variant="subtitle1"
            color={colors.red[90]}
            fontWeight={fontWeight[4]}
            pr={2}
            sx={Asterisk}
          >
            *
          </Typography>
        )}
      </Grid>
    );
  };

  return (
    <Box ref={formRef}>
      <Grid container style={{ height: "60vh" }} sx={flexCenter}>
        <Grid size={{ xs: 6 }} sx={flexCenter}>
          <Grid container pb="1.8rem">
            <Grid size={{ xs: 3 }} sx={flexRow}>
              {label("Email")}
            </Grid>
            <Grid size={{ xs: 9 }}>
              <Typography variant="h4" fontWeight={fontWeight[6]}>
                {loggedInUser.providerPortalUser.email}
              </Typography>
            </Grid>
          </Grid>
          <Grid container sx={GridContainer}>
            <Grid size={{ xs: 3 }} sx={flexRow}>
              {label("First Name", true)}
            </Grid>
            <Grid size={{ xs: 9 }}>
              <FormControl variant="standard">
                <FormInputText
                  name="providerPortalUser.firstName"
                  control={control}
                  helper={rules.firstName}
                  style={InputStyle}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container sx={GridContainer}>
            <Grid size={{ xs: 3 }} sx={flexRow}>
              {label("Last Name", true)}
            </Grid>
            <Grid size={{ xs: 9 }}>
              <FormControl variant="standard">
                <FormInputText
                  name="providerPortalUser.lastName"
                  control={control}
                  helper={rules.lastName}
                  style={InputStyle}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container sx={GridContainer}>
            <Grid size={{ xs: 3 }} sx={flexRow}>
              {label("Phone", true)}
            </Grid>
            <Grid size={{ xs: 9 }}>
              <FormControl variant="standard">
                <PhoneNumberInput
                  name="providerPortalUser.phoneNumber"
                  control={control}
                  helper={rules.phoneNumber}
                  style={PhoneNumberInputStyle}
                  disabled={false}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid size={{ xs: 15 }} pb="0.7rem">
            <Typography
              variant="body2"
              color={colors.black[17]}
              fontWeight={fontWeight[3]}
              lineHeight="0.8rem"
            >
              {NotificationLabel}
            </Typography>
          </Grid>
          <Grid container sx={GridContainer}>
            <Grid size={{ xs: 3 }} sx={flexRow}>
              {label("MFA Preference", true)}
            </Grid>
            <Grid size={{ xs: 9 }}>
              {!checkIfSSOUser() &&
              <FormSelect
                name="providerPortalUser.mfaId"
                list={mfaPreferenceOptions}
                control={control}
                helper={rules.mfaTypeRequired}
                style={SelectInputStyle}
              />}
              {checkIfSSOUser() &&
              <FormSelect
                name="providerPortalUser.mfaSSO"
                list={ssoMfaPreferenceOption}
                control={control}
                helper={rules.mfaTypeRequired}
                style={SelectInputStyle}
                disabled={true}
              />}
            </Grid>
          </Grid>
          <Grid container sx={flexCenter}>
            <Grid size={{ xs: 6 }} sx={alignStart}>
              <Button onClick={submit} variant="contained">
                Update
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MyAccount;
